import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDateRelative } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, LineChart, Line } from "recharts";
import { Badge } from "../components/ui/badge";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, Briefcase, PoundSterling, Activity, AlertTriangle, CheckCircle2, Wallet, Target, Mail, Sparkles, ArrowRight, Plus, ShieldAlert, ShieldCheck, ShieldOff, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function Dashboard() {
    const [kpi, setKpi] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sendingDigest, setSendingDigest] = useState(false);
    const [matches, setMatches] = useState(null);
    const [healthSummary, setHealthSummary] = useState(null);
    const [leakage, setLeakage] = useState(null);
    const [firmHealth, setFirmHealth] = useState(null);
    const [firmHistory, setFirmHistory] = useState([]);
    const [referrers, setReferrers] = useState(null);
    const nav = useNavigate();

    useEffect(() => {
        api.get("/dashboard/kpis").then((r) => setKpi(r.data)).catch(() => {}).finally(() => setLoading(false));
        api.get("/dashboard/unactioned-matches").then((r) => setMatches(r.data)).catch(() => setMatches({ matches: [], more_count: 0 }));
        api.get("/deals/health-summary").then((r) => setHealthSummary(r.data)).catch(() => setHealthSummary([]));
        api.get("/intelligence/leakage/summary").then((r) => setLeakage(r.data)).catch(() => setLeakage(null));
        api.get("/intelligence/firm-health").then((r) => setFirmHealth(r.data)).catch(() => {});
        api.get("/intelligence/firm-health/history").then((r) => setFirmHistory(r.data || [])).catch(() => {});
        api.get("/contacts/top-referrers", { params: { limit: 5 } }).then((r) => setReferrers(r.data || [])).catch(() => setReferrers([]));
    }, []);

    const refreshMatches = () => {
        api.get("/dashboard/unactioned-matches").then((r) => setMatches(r.data));
    };

    const addToPipeline = async (m) => {
        try {
            await api.post(`/deals/${m.deal_id}/buyer-matches/add-to-pipeline`, { buyer_ids: [m.buyer_id] });
            toast.success(`${m.buyer_name} added to ${m.deal_name}`);
            refreshMatches();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        }
    };

    const sendDigest = async () => {
        setSendingDigest(true);
        try {
            const { data } = await api.post("/email/digest/me");
            toast.success(`Digest sent to ${data.sent_to}`);
        } catch (err) {
            if (err.response?.status === 503) {
                toast.error("Resend not configured. Add RESEND_API_KEY to backend/.env to enable email.");
            } else {
                toast.error(err.response?.data?.detail || err.message);
            }
        } finally {
            setSendingDigest(false);
        }
    };

    if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading dashboard…</div>;
    if (!kpi) return <div className="p-8 text-sm text-red-500" data-testid="dashboard-error">Failed to load dashboard data. Please refresh the page.</div>;

    const chartData = (kpi?.by_stage || []).map((s) => ({
        name: s.stage.length > 10 ? s.stage.slice(0, 10) + "…" : s.stage,
        full: s.stage,
        count: s.count,
        value: s.value,
    }));

    return (
        <div data-testid="dashboard-page">
            <PageHeader title="Dashboard" subtitle="Snapshot of your M&A pipeline.">
                <Button
                    onClick={sendDigest}
                    disabled={sendingDigest}
                    variant="outline"
                    size="sm"
                    data-testid="send-digest-btn"
                >
                    <Mail className="w-3.5 h-3.5 mr-1.5" />
                    {sendingDigest ? "Sending…" : "Email me my digest"}
                </Button>
            </PageHeader>

            <div className="p-6 lg:p-8 space-y-8">
                <motion.div
                    className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
                    initial="hidden"
                    animate="show"
                    variants={{
                        hidden: {},
                        show: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } },
                    }}
                >
                    {[
                        { label: "Active Deals", value: kpi.active_deals, icon: Briefcase, testid: "stat-active-deals" },
                        { label: "Pipeline Value", value: fmtMoney(kpi.pipeline_value), icon: PoundSterling, testid: "stat-pipeline-value", mono: true, accent: "gold" },
                        { label: "Weighted Value", value: fmtMoney(kpi.weighted_pipeline_value), icon: TrendingUp, testid: "stat-weighted-value", mono: true, accent: "gold" },
                        { label: "Fees YTD", value: fmtMoney(kpi.fees_earned_ytd || 0), icon: Wallet, testid: "stat-fees-ytd", mono: true, accent: "gold" },
                        { label: "Projected Fee", value: fmtMoney(kpi.projected_pipeline_fee || 0), icon: Target, testid: "stat-projected-fee", mono: true, accent: "gold" },
                        { label: "Conversion", value: `${kpi.conversion_rate}%`, icon: CheckCircle2, testid: "stat-conversion", mono: true },
                    ].map((s) => (
                        <motion.div
                            key={s.testid}
                            variants={{
                                hidden: { opacity: 0, y: 12 },
                                show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
                            }}
                        >
                            <StatCard {...s} />
                        </motion.div>
                    ))}
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 border border-[#E2E8F0] rounded-[6px] bg-white p-5" style={{boxShadow:'0 1px 2px rgba(0,0,0,0.05)'}}>
                        <div className="flex items-baseline justify-between mb-5">
                            <div>
                                <h2 className="text-[15px] font-semibold text-[#0B1F3A]">Pipeline by stage</h2>
                                <p className="text-[12px] text-[#64748B] mt-0.5">Deal count per stage</p>
                            </div>
                            <button onClick={() => nav("/pipeline")} className="text-[11px] uppercase tracking-wider text-[#94A3B8] hover:text-[#0B1F3A] transition-colors" data-testid="view-pipeline-link">
                                View pipeline →
                            </button>
                        </div>
                        <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: -20 }}>
                                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94A3B8", fontFamily: 'Inter' }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: "#94A3B8", fontFamily: 'Inter' }} axisLine={false} tickLine={false} />
                                    <Tooltip
                                        cursor={{ fill: "#F8FAFC" }}
                                        contentStyle={{ fontSize: 12, border: "1px solid #E2E8F0", borderRadius: 6, fontFamily: 'Inter', boxShadow: '0 2px 4px rgba(0,0,0,0.06)' }}
                                        labelFormatter={(l, p) => p?.[0]?.payload?.full || l}
                                    />
                                    <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                                        {chartData.map((_, i) => <Cell key={i} fill="#0B1F3A" />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="border border-[#E2E8F0] rounded-[6px] bg-white overflow-hidden" style={{boxShadow:'0 1px 2px rgba(0,0,0,0.05)'}}>
                        <div className="px-4 py-3 border-b border-[#E2E8F0]">
                            <h2 className="text-[15px] font-semibold text-[#0B1F3A]">Quick metrics</h2>
                        </div>
                        <div className="divide-y divide-[#F1F5F9]" data-testid="quick-metrics">
                            <Row label="Total deals" value={kpi.total_deals} mono />
                            <Row label="Won" value={kpi.won} mono accent="success" />
                            <Row label="Lost" value={kpi.lost} mono accent="destructive" />
                            <Row label="Avg. duration" value={`${kpi.avg_deal_duration_days}d`} mono />
                            <Row label="Stale (>14d)" value={kpi.stale_count} mono accent={kpi.stale_count > 0 ? "warning" : undefined} />
                        </div>
                    </div>
                </div>

                <UnactionedMatchesCard data={matches} onAdd={addToPipeline} />

                <FirmHealthCard data={firmHealth} history={firmHistory} onNavigate={() => nav("/managing-partner")} />

                <PortfolioHealthCard summary={healthSummary} onNavigate={() => nav("/pipeline")} />

                <RevenueAtRiskCard data={leakage} onNavigate={() => nav("/intelligence/leakage")} />

                <TopReferrersCard data={referrers} onNavigate={(id) => nav(`/contacts/${id}`)} onViewAll={() => nav("/contacts")} />

                <div className="border border-[#E2E8F0] rounded-[6px] bg-white p-5" style={{boxShadow:'0 1px 2px rgba(0,0,0,0.05)'}}>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-[15px] font-semibold text-[#0B1F3A]">Recent activity</h2>
                        <Badge variant="outline" className="font-mono text-[10px]">
                            <Activity className="w-3 h-3 mr-1" /> Live
                        </Badge>
                    </div>
                    {kpi.recent_activity.length === 0 ? (
                        <EmptyActivity />
                    ) : (
                        <ul className="divide-y divide-[#F1F5F9]" data-testid="recent-activity-list">
                            {kpi.recent_activity.map((a) => (
                                <li key={a.id} className="py-2.5 flex items-center justify-between gap-4">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-1.5 h-1.5 rounded-full bg-[#0B1F3A] flex-shrink-0" />
                                        <div className="truncate text-[13px] text-[#0B1F3A]">
                                            <span className="text-[#94A3B8] uppercase tracking-wider text-[10px] mr-2">{a.type}</span>
                                            {a.content}
                                        </div>
                                    </div>
                                    <span className="text-[11px] text-[#94A3B8] font-mono flex-shrink-0">{fmtDateRelative(a.created_at)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

function Row({ label, value, mono, accent }) {
    const colorMap = {
        success: "#15803D",
        destructive: "#B91C1C",
        warning: "#B45309",
    };
    const valueColor = colorMap[accent] || "#0B1F3A";
    return (
        <div className="flex items-center justify-between px-4 py-3">
            <span className="text-[13px] text-[#64748B]">{label}</span>
            <span
                className={`text-[13px] font-semibold ${mono ? "font-mono" : ""}`}
                style={{ color: valueColor }}
            >
                {value}
            </span>
        </div>
    );
}

function EmptyActivity() {
    return (
        <div className="text-center py-10 text-muted-foreground">
            <AlertTriangle className="w-5 h-5 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No activity yet. Create a deal to get started.</p>
        </div>
    );
}


function PortfolioHealthCard({ summary, onNavigate }) {
    if (!summary) return null;
    const scored = summary.filter(d => d.health_score != null);
    const critical = scored.filter(d => d.health_band === "critical").length;
    const atRisk = scored.filter(d => d.health_band === "at_risk").length;
    const healthy = scored.filter(d => d.health_band === "healthy").length;
    const unscored = summary.length - scored.length;

    return (
        <div className="border border-[#E2E8F0] rounded-[6px] bg-white p-5" style={{boxShadow:'0 1px 2px rgba(0,0,0,0.05)'}} data-testid="portfolio-health-card">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="text-[15px] font-semibold text-[#0B1F3A]">Portfolio Health</h2>
                    <p className="text-[12px] text-[#64748B] mt-0.5">
                        Health scores for {summary.length} active deal{summary.length !== 1 ? "s" : ""}
                        {unscored > 0 ? ` · ${unscored} not yet scored` : ""}
                    </p>
                </div>
                <button
                    onClick={onNavigate}
                    className="text-[11px] uppercase tracking-wider text-[#94A3B8] hover:text-[#0B1F3A] transition-colors"
                    data-testid="health-view-pipeline-link"
                >
                    View pipeline →
                </button>
            </div>
            <div className="grid grid-cols-3 gap-3" data-testid="health-summary-bands">
                <div className="flex items-center gap-2.5 p-3 rounded-[4px] bg-red-50 border border-red-100" data-testid="health-band-critical">
                    <ShieldAlert className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <div>
                        <div className="text-xl font-semibold font-mono text-red-600">{critical}</div>
                        <div className="text-[10px] text-red-500 uppercase tracking-wider">Critical</div>
                    </div>
                </div>
                <div className="flex items-center gap-2.5 p-3 rounded-[4px] bg-amber-50 border border-amber-100" data-testid="health-band-at-risk">
                    <ShieldOff className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <div>
                        <div className="text-xl font-semibold font-mono text-amber-600">{atRisk}</div>
                        <div className="text-[10px] text-amber-600 uppercase tracking-wider">At Risk</div>
                    </div>
                </div>
                <div className="flex items-center gap-2.5 p-3 rounded-[4px] bg-green-50 border border-green-100" data-testid="health-band-healthy">
                    <ShieldCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <div>
                        <div className="text-xl font-semibold font-mono text-green-600">{healthy}</div>
                        <div className="text-[10px] text-green-600 uppercase tracking-wider">Healthy</div>
                    </div>
                </div>
            </div>
            {scored.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[#F1F5F9]">
                    <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] mb-2">Worst deals first</div>
                    <ul className="space-y-1.5">
                        {scored.slice(0, 3).map(d => (
                            <li key={d.id} className="flex items-center justify-between text-[13px]">
                                <Link to={`/deals/${d.id}`} className="truncate hover:text-gold transition-colors font-medium max-w-[60%]">{d.company_name}</Link>
                                <HealthBadge score={d.health_score} band={d.health_band} />
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}


function HealthBadge({ score, band }) {
    if (score == null) return <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">—</span>;
    const cls = {
        healthy: "bg-green-100 text-green-700",
        at_risk: "bg-amber-100 text-amber-700",
        critical: "bg-red-100 text-red-700",
    }[band] || "bg-gray-100 text-gray-500";
    return <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${cls}`} data-testid={`health-badge-${band}`}>{score}</span>;
}


function RevenueAtRiskCard({ data, onNavigate }) {
    if (!data) return null;
    const fmtGBP = (n) => n > 0 ? `£${Number(n).toLocaleString("en-GB", { maximumFractionDigits: 0 })}` : "£0";
    const rows = [
        { label: "Dormant deals",       count: data.count_dormant,             color: "text-red-600",   dot: "bg-red-500" },
        { label: "Uncontacted matches", count: data.count_uncontacted_matches,  color: "text-amber-600", dot: "bg-amber-400" },
        { label: "Stale follow-ups",    count: data.count_stale_buyers,         color: "text-red-600",   dot: "bg-red-400" },
        { label: "Fees not set",        count: data.count_missing_fees,         color: "text-blue-600",  dot: "bg-blue-400" },
    ];
    return (
        <div
            className="border border-[#E2E8F0] rounded-[6px] bg-white p-5 cursor-pointer hover:shadow-md transition-shadow"
            style={{boxShadow:'0 1px 2px rgba(0,0,0,0.05)'}}
            onClick={onNavigate}
            data-testid="revenue-at-risk-card"
        >
            <div className="flex items-center justify-between mb-1">
                <h2 className="text-[15px] font-semibold text-[#0B1F3A]">Revenue at Risk</h2>
                <span className="text-[11px] uppercase tracking-wider text-[#94A3B8] hover:text-[#0B1F3A]" data-testid="leakage-view-link">View details →</span>
            </div>
            <div className="text-2xl font-semibold font-mono text-[#B91C1C] mb-4" data-testid="dashboard-total-at-risk">{fmtGBP(data.total_fee_at_risk)}</div>
            <ul className="space-y-2" data-testid="leakage-sub-rows">
                {rows.map(r => (
                    <li key={r.label} className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-[13px] text-[#64748B]">
                            <span className={`w-1.5 h-1.5 rounded-full ${r.dot} flex-shrink-0`} />
                            {r.label}
                        </span>
                        <span className={`font-mono font-medium text-[13px] ${r.color}`}>{r.count}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}


function TopReferrersCard({ data, onNavigate, onViewAll }) {
    // Hidden until at least one deal has an introducer — an empty card is just noise.
    if (!data || data.length === 0) return null;
    return (
        <div className="border border-[#E2E8F0] rounded-[6px] bg-white overflow-hidden" style={{boxShadow:'0 1px 2px rgba(0,0,0,0.05)'}} data-testid="top-referrers-card">
            <div className="flex items-baseline justify-between px-5 py-3 border-b border-[#E2E8F0]">
                <div>
                    <h2 className="text-[15px] font-semibold text-[#0B1F3A]">Top referrers</h2>
                    <p className="text-[12px] text-[#64748B] mt-0.5">Introducers ranked by completed deals</p>
                </div>
                <button onClick={onViewAll} className="text-[11px] uppercase tracking-wider text-[#94A3B8] hover:text-[#0B1F3A] transition-colors" data-testid="top-referrers-view-all">
                    All contacts →
                </button>
            </div>
            <table className="w-full text-[13px]">
                <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-[#94A3B8]">
                        <th className="px-5 py-2 font-medium">Referrer</th>
                        <th className="px-3 py-2 font-medium text-right">Referred</th>
                        <th className="px-3 py-2 font-medium text-right">Completed</th>
                        <th className="px-3 py-2 font-medium text-right">Rate</th>
                        <th className="px-5 py-2 font-medium text-right">Fees</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]" data-testid="top-referrers-rows">
                    {data.map((r) => (
                        <tr key={r.contact_id} className="hover:bg-[#F8FAFC] cursor-pointer" onClick={() => onNavigate(r.contact_id)} data-testid={`top-referrer-${r.contact_id}`}>
                            <td className="px-5 py-2">
                                <div className="font-medium text-[#0B1F3A]">{r.name}</div>
                                {r.company && <div className="text-[12px] text-[#64748B]">{r.company}</div>}
                            </td>
                            <td className="px-3 py-2 text-right font-mono">{r.deals_referred}</td>
                            <td className="px-3 py-2 text-right font-mono text-[#15803D]">{r.deals_completed}</td>
                            <td className="px-3 py-2 text-right font-mono text-[#64748B]">{r.completion_rate}%</td>
                            <td className="px-5 py-2 text-right font-mono">
                                {r.deals_with_known_fee > 0 ? fmtMoney(r.total_fees_from_referrals) : "—"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}


function UnactionedMatchesCard({ data, onAdd }) {
    const [sending, setSending] = useState(false);
    if (!data) return null;
    const matches = data.matches || [];

    const emailMe = async () => {
        setSending(true);
        try {
            const { data: r } = await api.post("/email/matches-digest/me");
            toast.success(`Digest sent to ${r.sent_to}`);
        } catch (err) {
            if (err.response?.status === 503) {
                toast.error("Email not configured yet. Add RESEND_API_KEY to backend/.env to enable.");
            } else {
                toast.error(err.response?.data?.detail || err.message);
            }
        } finally {
            setSending(false);
        }
    };
    return (
        <div className="border border-[#C9A227]/30 rounded-[6px] bg-white p-5" style={{boxShadow:'0 1px 2px rgba(0,0,0,0.05)'}} data-testid="unactioned-matches-card">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="text-[15px] font-semibold text-[#0B1F3A] flex items-center gap-2">
                        <Sparkles className="w-3.5 h-3.5 text-[#C9A227]" /> Unactioned Buyer Matches
                    </h2>
                    <p className="text-[12px] text-[#64748B] mt-0.5">
                        High-fit buyers (score ≥ 40) not yet added to the pipeline.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono text-[10px]">
                        {matches.length}{data.more_count > 0 ? ` of ${matches.length + data.more_count}` : ""}
                    </Badge>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={emailMe}
                        disabled={sending}
                        className="gap-1.5"
                        data-testid="email-matches-digest-btn"
                    >
                        <Mail className="w-3 h-3" /> {sending ? "Sending…" : "Email me this list"}
                    </Button>
                </div>
            </div>
            {matches.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground" data-testid="unactioned-matches-empty">
                    <CheckCircle2 className="w-5 h-5 mx-auto mb-2 opacity-50 text-emerald-600" />
                    <p className="text-sm">All caught up — every high-fit buyer match is already on a deal.</p>
                </div>
            ) : (
                <ul className="divide-y divide-border/60" data-testid="unactioned-matches-list">
                    {matches.map((m) => (
                        <li
                            key={`${m.deal_id}-${m.buyer_id}`}
                            className="py-3 flex items-center gap-3 hover:bg-muted/20 -mx-2 px-2 rounded-sm transition-colors"
                            data-testid={`unactioned-match-${m.buyer_id}-${m.deal_id}`}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap text-sm">
                                    <span className="font-medium">{m.buyer_name}</span>
                                    {m.buyer_company && <span className="text-muted-foreground text-xs">· {m.buyer_company}</span>}
                                    <ArrowRight className="w-3 h-3 text-muted-foreground" />
                                    <Link
                                        to={`/deals/${m.deal_id}`}
                                        className="text-foreground hover:text-gold transition-colors font-display font-bold"
                                    >
                                        {m.deal_name}
                                    </Link>
                                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">{m.deal_stage}</span>
                                </div>
                                {m.reasons?.length > 0 && (
                                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{m.reasons.join(" · ")}</div>
                                )}
                            </div>
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 font-mono">
                                Score {m.score}
                            </Badge>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onAdd(m)}
                                className="gap-1"
                                data-testid={`unactioned-match-add-${m.buyer_id}-${m.deal_id}`}
                            >
                                <Plus className="w-3 h-3" /> Add to pipeline
                            </Button>
                        </li>
                    ))}
                </ul>
            )}
            {data.more_count > 0 && (
                <div className="pt-3 mt-2 border-t border-border/60 text-center">
                    <span className="text-xs text-muted-foreground">
                        {data.more_count} more unactioned match{data.more_count === 1 ? "" : "es"} across other deals.{" "}
                    </span>
                    {matches[0] && (
                        <Link
                            to={`/deals/${matches[0].deal_id}`}
                            className="text-xs font-medium text-gold hover:text-amber-700 underline"
                            data-testid="unactioned-matches-view-all"
                        >
                            View all matches
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
}



const BAND = {
    healthy:  { text: "text-emerald-700", bg: "bg-emerald-50",  border: "border-emerald-100", dot: "bg-emerald-500"  },
    at_risk:  { text: "text-amber-700",   bg: "bg-amber-50",    border: "border-amber-100",   dot: "bg-amber-500"    },
    critical: { text: "text-red-700",     bg: "bg-red-50",      border: "border-red-100",     dot: "bg-red-500"      },
};

function FirmHealthCard({ data, history, onNavigate }) {
    if (!data) return null;
    const c = BAND[data.band] || BAND.at_risk;
    const spark = (history || []).slice(0, 7).reverse().map((h, i) => ({ i, score: h.firm_health_score }));
    return (
        <div
            className={`border rounded-[6px] p-5 cursor-pointer hover:shadow-md transition-shadow ${c.bg} ${c.border}`}
            onClick={onNavigate}
            data-testid="dashboard-firm-health-card"
        >
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" style={{color:'#64748B'}} />
                    <h2 className="text-[15px] font-semibold text-[#0B1F3A]">Firm Health Score</h2>
                </div>
                <span className="text-[11px] uppercase tracking-wider text-[#94A3B8]">Full view →</span>
            </div>
            <div className="flex items-end gap-6">
                <div>
                    <div className={`text-4xl font-semibold font-mono ${c.text}`} data-testid="dashboard-firm-health-score">
                        {data.firm_health_score}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                        <span className={`text-[11px] font-medium uppercase tracking-wider ${c.text}`} data-testid="dashboard-firm-health-band">
                            {data.band.replace("_", " ")}
                        </span>
                    </div>
                </div>
                {spark.length >= 2 && (
                    <div className="flex-1 h-12 hidden sm:block">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={spark}>
                                <Line type="monotone" dataKey="score" stroke={data.band === "healthy" ? "#15803D" : data.band === "at_risk" ? "#B45309" : "#B91C1C"} strokeWidth={1.5} dot={false} />
                                <YAxis domain={[0, 100]} hide />
                                <Tooltip
                                    contentStyle={{ fontSize: 10, border: "1px solid #E2E8F0", borderRadius: 4, fontFamily: 'Inter' }}
                                    formatter={(v) => [`${v}/100`, "Score"]}
                                    labelFormatter={() => ""}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
}
